import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { eventsRoute } from 'basics/constants/routes.constants';
import useStepIndexer from 'hooks/useStepIndexer';
import { commonFields } from 'modules/events/basics/constants/commonFormFields.constants';
import { Event } from 'modules/events/basics/types/events.types';
import { usePublishOnlineMeetingMutation } from 'modules/events/graphql/mutations/publishOnlineMeeting';
import { useUpdateOnlineMeetingMutation } from 'modules/events/graphql/mutations/updateOnlineMeeting';
import { createOnlineMeetingStepIndexer } from 'modules/events/routes/CreateOnlineMeeting/CreateOnlineMeeting.states';
import { onlineMeetingInputTransformer }
  from 'modules/events/routes/CreateOnlineMeeting/CreateOnlineMeeting.transformers';
import {
  CreateOnlineMeetingStepsIndex,
  CreateOnlineMeetingWithEmailConfigurationStepsIndex,
} from 'modules/events/routes/CreateOnlineMeeting/CreateOnlineMeeting.types';
import eventState from 'modules/events/states/events.states';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useCreateOnlineMeetingActions = (yodaFormMethods: any) => {
  const navigate = useNavigate();
  const stepIndexer = useStepIndexer(createOnlineMeetingStepIndexer);
  const { stepIndex, moveNextStep, movePreviousStep } = stepIndexer;
  const { getValues, getDirtyValues, formState, setStepShowError, setFormIsDirty } = yodaFormMethods;
  const [publishOnlineMeeting, { loading: publishLoading }] = usePublishOnlineMeetingMutation();
  const [updateOnlineMeeting, { loading: updateLoading }] = useUpdateOnlineMeetingMutation();
  const flags = useFlags();

  const getOnlineMeetingFormData = useCallback(() => {
    const formValues = getValues();
    const dataToSave = getDirtyValues();
    return {
      onlineMeetingInput: onlineMeetingInputTransformer(dataToSave, formValues),
      onlineMeetingFormat: dataToSave[commonFields.eventFormat] ?? null,
    };
  }, [getDirtyValues, getValues]);

  const updateEventState = useCallback((updatedEvent?: Event | null) => {
    if (updatedEvent) {
      eventState.createEvent.updatedAt = updatedEvent.updatedAt;
      eventState.createEvent.status = updatedEvent.status;
    }
  }, []);

  const handleSaveData = useCallback(async () => {
    if (formState.isDirty) {
      const onlineMeetingFormData = getOnlineMeetingFormData();
      if (onlineMeetingFormData.onlineMeetingInput) {
        const updatedMeeting = await updateOnlineMeeting(
          onlineMeetingFormData.onlineMeetingInput,
          onlineMeetingFormData.onlineMeetingFormat,
        );
        setFormIsDirty(false);
        updateEventState(updatedMeeting);
      }
    }
  }, [formState.isDirty, getOnlineMeetingFormData, updateOnlineMeeting, setFormIsDirty, updateEventState]);

  const handlePublishEvent = useCallback(async () => {
    if (formState.isValid) {
      const onlineMeetingFormData = getOnlineMeetingFormData();
      if (onlineMeetingFormData.onlineMeetingInput) {
        const publishedMeeting = await publishOnlineMeeting(onlineMeetingFormData.onlineMeetingInput);
        if (publishedMeeting) {
          updateEventState(publishedMeeting);
          navigate(eventsRoute);
        }
      }
    }
  }, [formState.isValid, getOnlineMeetingFormData, navigate, publishOnlineMeeting, updateEventState]);

  const setShowError = useCallback((stepIndexVal: number) => {
    const step = flags.eventsBiogenlinc4151SectionEmail
      ? CreateOnlineMeetingWithEmailConfigurationStepsIndex[stepIndexVal] : CreateOnlineMeetingStepsIndex[stepIndexVal];
    setStepShowError(true, [step]);
  }, [flags.eventsBiogenlinc4151SectionEmail, setStepShowError]);

  const handleChangeStep = useCallback((stepIndexVal: number) => {
    const previousStep = stepIndexer.previousStep || 0;
    stepIndexer.moveToStep(stepIndexVal);
    handleSaveData();
    setShowError(previousStep);
  }, [handleSaveData, setShowError, stepIndexer]);

  const saveAndNext = useCallback(async () => {
    await handleSaveData();
    setShowError(stepIndex);
    moveNextStep();
  }, [handleSaveData, moveNextStep, setShowError, stepIndex]);

  const saveAndPrevious = useCallback(async () => {
    await handleSaveData();
    setShowError(stepIndex);
    movePreviousStep();
  }, [handleSaveData, movePreviousStep, setShowError, stepIndex]);

  return {
    handleChangeStep,
    handlePublishEvent,
    handleSaveData,
    setShowError,
    publishLoading,
    updateLoading,
    stepIndexer,
    saveAndNext,
    saveAndPrevious,
  };
};

export default useCreateOnlineMeetingActions;
